import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import axios from "axios";
import { CreditCard, Globe, CheckCircle2, AlertCircle, RefreshCw, Wrench, Activity, AlertTriangle, Key } from "lucide-react";
import { Button } from "../../components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StripeDiagnosticPanel = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [repairing, setRepairing] = useState(false);

  const authHeaders = useCallback(() => {
    const token = localStorage.getItem("sa_token");
    return { Authorization: `Bearer ${token}` };
  }, []);

  const fetchAll = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/superadmin/stripe/diagnostic`, { headers: authHeaders() });
      setData(res.data);
    } catch (e) {
      toast.error("Errore caricamento diagnostica");
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const handleRepair = async () => {
    if (!window.confirm("Eseguire il backfill di package_ids, telegram_addons e prezzi Telegram per tutte le subscription orfane?")) return;
    setRepairing(true);
    try {
      const [r1, r2] = await Promise.all([
        axios.post(`${API}/superadmin/stripe/repair-missing-packages`, {}, { headers: authHeaders() }),
        axios.post(`${API}/superadmin/pricing/repair-telegram-fields`, {}, { headers: authHeaders() }),
      ]);
      let msg = `✓ Backfill: ${r1.data.package_ids_backfilled} pacchetti, ${r1.data.telegram_addons_backfilled} addon TG, ${r1.data.stripe_data_reconciled || 0} riconciliate con Stripe`;
      if (r2.data.changed) {
        msg += ` · Prezzo Telegram allineato (${r2.data.old_prezzo_mensile} → ${r2.data.new_prezzo_mensile})`;
      }
      toast.success(msg);
      fetchAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally {
      setRepairing(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <div className="w-8 h-8 border-2 border-purple-200 border-t-purple-500 rounded-full animate-spin" />
      </div>
    );
  }

  const s = data?.stripe || {};
  const a = data?.anomalies || {};
  const events = data?.events?.recent || [];

  const keyModeBadge = {
    "live": <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700 text-[10px] font-bold border border-red-200">LIVE</span>,
    "test": <span className="px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 text-[10px] font-bold border border-amber-200">TEST</span>,
    "missing": <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-500 text-[10px] font-bold border border-gray-200">NON CONFIGURATA</span>,
    "unknown": <span className="px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 text-[10px] font-bold border border-purple-200">UNKNOWN</span>,
  }[s.api_key_mode] || null;

  const totalAnomalies = (a.subscriptions_without_customer_id || 0) + (a.subscriptions_with_empty_packages || 0);

  return (
    <div className="space-y-5" data-testid="stripe-diagnostic-panel">
      <div>
        <h2 className="text-2xl font-black tracking-tight text-slate-900">Diagnostica Stripe</h2>
        <p className="text-sm text-gray-500 mt-1">Stato configurazione, webhook e anomalie sui dati di pagamento</p>
      </div>

      {/* Config */}
      <div className="rounded-2xl border border-gray-200 bg-white p-5 space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
            <Key className="h-5 w-5 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-base font-bold text-gray-900">Configurazione</h3>
          </div>
          {keyModeBadge}
        </div>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div className="p-3 rounded-xl bg-gray-50 border border-gray-100">
            <p className="text-[10px] uppercase tracking-wider font-bold text-gray-400">API Key</p>
            <p className="font-mono text-xs text-gray-700 mt-1 break-all">{s.api_key_preview || "(mancante)"}</p>
          </div>
          <div className="p-3 rounded-xl bg-gray-50 border border-gray-100">
            <p className="text-[10px] uppercase tracking-wider font-bold text-gray-400">Webhook Secret</p>
            <p className="text-xs text-gray-700 mt-1 font-bold">
              {s.webhook_secret_set
                ? <span className="text-emerald-600">Configurato ✓</span>
                : <span className="text-red-500">MANCANTE</span>}
            </p>
          </div>
          <div className="col-span-2 p-3 rounded-xl bg-blue-50 border border-blue-100">
            <p className="text-[10px] uppercase tracking-wider font-bold text-blue-700">URL Webhook atteso (da configurare su Stripe Dashboard)</p>
            <code className="block mt-1 text-xs text-blue-900 font-mono break-all">{s.expected_webhook_url || "(REACT_APP_BACKEND_URL mancante)"}</code>
            <p className="text-[11px] text-blue-600 mt-1.5">
              Ambiente: {s.is_production_url
                ? <span className="font-bold text-red-600">PRODUZIONE</span>
                : <span className="font-bold">PREVIEW</span>}
            </p>
          </div>
        </div>
      </div>

      {/* Activity */}
      <div className="rounded-2xl border border-gray-200 bg-white p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center">
            <Activity className="h-5 w-5 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-base font-bold text-gray-900">Eventi Webhook ricevuti</h3>
            <p className="text-xs text-gray-400">
              {data?.events?.total || 0} totali · <span className="font-bold">{data?.events?.last_24h || 0}</span> nelle ultime 24h
            </p>
          </div>
        </div>
        {events.length === 0 ? (
          <div className="p-4 rounded-xl bg-amber-50 border border-amber-200">
            <p className="text-sm font-bold text-amber-800">⚠ Nessun evento webhook ricevuto</p>
            <p className="text-xs text-amber-700 mt-1">
              Configura il webhook su Stripe Dashboard verso l&apos;URL sopra. Eventi necessari: <code className="px-1 bg-amber-100 rounded">checkout.session.completed</code>, <code className="px-1 bg-amber-100 rounded">customer.subscription.*</code>, <code className="px-1 bg-amber-100 rounded">invoice.payment_succeeded</code>, <code className="px-1 bg-amber-100 rounded">invoice.payment_failed</code>
            </p>
          </div>
        ) : (
          <div className="space-y-1 max-h-[260px] overflow-y-auto">
            {events.map((ev, i) => (
              <div key={i} className="p-2 rounded-lg bg-gray-50 hover:bg-gray-100 text-xs space-y-1" data-testid={`event-${i}`}>
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full flex-shrink-0 ${ev.livemode ? "bg-red-500" : "bg-amber-400"}`} />
                  <span className="font-mono text-gray-500 text-[10px] w-32 flex-shrink-0 truncate">{ev.received_at ? new Date(ev.received_at).toLocaleString("it-IT") : ""}</span>
                  <span className="font-bold text-gray-800 flex-1 truncate">{ev.event_type}</span>
                  {ev.customer && <code className="text-[10px] text-gray-400 font-mono">{ev.customer}</code>}
                </div>
                {ev.diag && (
                  <div className="ml-4 flex flex-wrap items-center gap-1.5 text-[10px]">
                    <span className={`px-1.5 py-0.5 rounded font-bold ${ev.diag.session_retrieved ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`} title="Session retrieve">
                      {ev.diag.session_retrieved ? "✓" : "✗"} Session
                    </span>
                    <span className={`px-1.5 py-0.5 rounded font-bold ${ev.diag.tx_found ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`} title="Payment transaction trovata">
                      {ev.diag.tx_found ? "✓" : "✗"} TX
                    </span>
                    <span className={`px-1.5 py-0.5 rounded font-bold ${ev.diag.stripe_customer_resolved ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                      {ev.diag.stripe_customer_resolved ? "✓" : "✗"} Customer
                    </span>
                    <span className={`px-1.5 py-0.5 rounded font-bold ${ev.diag.stripe_subscription_resolved ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>
                      {ev.diag.stripe_subscription_resolved ? "✓" : "✗"} Sub
                    </span>
                    <span className={`px-1.5 py-0.5 rounded font-bold ${ev.diag.stripe_pm_resolved ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                      {ev.diag.stripe_pm_resolved ? "✓" : "✗"} PM
                    </span>
                    <span className={`px-1.5 py-0.5 rounded font-bold ${ev.diag.societa_updated ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                      {ev.diag.societa_updated ? "✓" : "✗"} Saved
                    </span>
                    {ev.diag.error && <span className="text-red-600 font-mono">⚠ {ev.diag.error}</span>}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Anomalies */}
      <div className="rounded-2xl border border-gray-200 bg-white p-5">
        <div className="flex items-center gap-3 mb-4">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${totalAnomalies > 0 ? "bg-gradient-to-br from-amber-500 to-orange-600" : "bg-gradient-to-br from-gray-400 to-gray-500"}`}>
            <AlertTriangle className="h-5 w-5 text-white" />
          </div>
          <div className="flex-1">
            <h3 className="text-base font-bold text-gray-900">Anomalie dati</h3>
            <p className="text-xs text-gray-400">Subscription con dati Stripe o pacchetti mancanti</p>
          </div>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {[
            ["Senza customer_id", a.subscriptions_without_customer_id],
            ["Senza subscription_id", a.subscriptions_without_subscription_id],
            ["Senza pacchetti", a.subscriptions_with_empty_packages],
            ["Pending pagamento", a.pending_registrations],
            ["Bloccate (activating)", a.activating_registrations],
          ].map(([label, val]) => (
            <div key={label} className={`p-3 rounded-xl border ${(val || 0) > 0 ? "bg-amber-50 border-amber-200" : "bg-gray-50 border-gray-100"}`}>
              <p className="text-[10px] uppercase tracking-wider font-bold text-gray-400">{label}</p>
              <p className={`text-2xl font-black mt-1 ${(val || 0) > 0 ? "text-amber-700" : "text-gray-400"}`}>{val ?? 0}</p>
            </div>
          ))}
        </div>
        {(a.subscriptions_with_empty_packages > 0 || (a.subscriptions_without_customer_id || 0) > 0) && (
          <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
            <p className="text-sm text-gray-600 max-w-md">
              Esegui il <b>backfill</b> per ripristinare i pacchetti scelti in registrazione e gli addon Telegram per le società colpite dal vecchio bug.
            </p>
            <Button onClick={handleRepair} disabled={repairing}
              className="rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-bold gap-2" data-testid="repair-btn">
              {repairing ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <Wrench className="h-4 w-4" />}
              Esegui backfill ora
            </Button>
          </div>
        )}
      </div>

      <div className="flex justify-end">
        <Button onClick={fetchAll} variant="outline" className="rounded-xl text-xs gap-1.5">
          <RefreshCw className="h-3.5 w-3.5" /> Aggiorna
        </Button>
      </div>
    </div>
  );
};

export default StripeDiagnosticPanel;
